import { Observable } from "rxjs";
import { Injectable } from "@angular/core";

import * as _ from "lodash";
import * as moment from "moment";

import { URLSearchParams } from '@angular/http'
import { HttpBackendService } from "app/services/http-backend.service";
import { Call } from "app/models/call";
import { PagedResult } from "app/models/PagedResult";
import { CallOutputDTO } from "app/dto/callOutputDTO";
import { Client } from "app/models/client";
import { ProblemTemplateService } from "app/services/problemTemplate.service";
import { WorkTask } from "app/models/workTask";
import { Work } from "app/models/work";
import { CallFilterOptions } from "app/dto/callFilterOptions";
import { AssetService } from "app/services/asset.service";
import { LocationService } from "app/services/location.service";
import { CallPhotoData } from "app/models/callPhotoData";
import { PhotoType } from "app/models/photoDataPhotoType";
import { Obstruction } from "../models/obstruction";
import { Report } from "app/models/report";
import { CallMessage } from "app/models/callMessage";
import { Survey } from "app/models/surveyScheme/survey";
import { BlockSection, Section, TableSection } from "app/models/surveyScheme/section";
import { title } from "process";
import { CalendarTimeField, Coordinate, Field, LocationField, PhotoField, PlainTextField, SignatureField, SurveyPhoto } from "app/models/surveyScheme/field";

@Injectable()
export class CallService {

  constructor(
    private _http: HttpBackendService
  ) { }

  public createCall(call: CallOutputDTO): Observable<CallOutputDTO> {
    let dto = {
      Description: call.description,
      ProblemTemplateId: call.problemTemplateId,
      ObjectIds: [call.serviceObjectId]
    };

    return this._http.post("/api/call/userCall", dto, null, true)
      .map(resp => {
        call.id = resp.json().Id;
        return call;
      });
  }

  public getCallDetails(callId: number): Observable<Call> {

    let params = new URLSearchParams;
    params.append('$select', "Id,Number,Status,CancelStatus,CreatedInFSM,Deadline,Closed,Description,ExternalNumber,Author,IsConfirmed");
    params.append('$expand', "ProblemTemplate,Reports,Photos,Messages($expand=Owner;$orderby=Date desc),Works($expand=Tasks,Object,Obstructions($expand=Reason))");

    return this._http.get("/api/odata/calls(" + callId + ")", params, true)
      .map(resp => {
        return CallService.convertCallDTO2Object(this._http, resp.json());
      });
  }

  public getCallAuthors(): Observable<Array<string>> {
    return this._http.get("/api/call/authors", null, true)
      .map(resp => {
        return resp.json();
      });
  }

  public getCalls(skip: number, count: number, sortBy: string, isDesc: boolean, filterOptions: CallFilterOptions): Observable<PagedResult<Call>> {

    let params = new URLSearchParams;
    let filterRequestStrings: string[] = new Array();

    params.append('$orderby', sortBy + (isDesc ? " desc" : " asc"));
    params.append('$select', "Id,Number,Status,CreatedInFSM,Deadline,Description,Author,IsConfirmed,CancelStatus");
    params.append('$expand', "Works($expand=Object,Obstructions)");
    params.append('$top', count ? count.toString() : null);
    params.append('$skip', skip ? skip.toString() : null);
    params.append('$count', "true");

    if (filterOptions) {

      if (filterOptions.problemTemplateIds)
        filterRequestStrings.push("ProblemTemplate/Id in (" + filterOptions.problemTemplateIds.toString() + ")");

      if (filterOptions.status)
        filterRequestStrings.push("Status eq ClientPersonalArea.Contracts.Model.CallStatus'" + filterOptions.status.toString() + "'");

      if (filterOptions.isConfirmed != null)
        filterRequestStrings.push("IsConfirmed eq " + !!filterOptions.isConfirmed);

      if (filterOptions.number)
        filterRequestStrings.push("contains(Number, '" + filterOptions.number + "')");

      if (filterOptions.author)
        filterRequestStrings.push("Author eq " + (filterOptions.author == "Диспетчер" ? "null" : ("'" + filterOptions.author) + "'")); //TODO: убрать условие

      if (filterOptions.createdRange) {
        if (filterOptions.createdRange.length >= 1 && filterOptions.createdRange[0])
          filterRequestStrings.push("CreatedInFSM ge " + moment(filterOptions.createdRange[0]).startOf('day').toISOString());

        if (filterOptions.createdRange.length == 2 && filterOptions.createdRange[1])
          filterRequestStrings.push("CreatedInFSM lt " + moment(filterOptions.createdRange[1]).startOf('day').add(1, 'days').toISOString());
      }

      if (filterRequestStrings.length > 0)
        params.append('$filter', _.join(filterRequestStrings, ' and '));
    }

    return this._http.get("/api/odata/calls", params, true)
      .map(resp => {
        let pagedResult = resp.json();
        let result = new PagedResult<Call>();
        result.items = [];

        for (let index in pagedResult.value) {
          result.items.push(CallService.convertCallDTO2Object(this._http, pagedResult.value[index]));
        }
        result.totalCount = pagedResult["@odata.count"];

        return result;
      });
  }

  public confirmCall(call: Call): Observable<Call> {
    return this._http.post("/api/call/" + call.id + "/confirm", null, null, true)
      .map(_ => {
        call.isConfirmed = true;
        return call;
      });
  }

  public cancelCall(call: Call): Observable<Call> {
    return this._http.post("/api/call/" + call.id + "/cancel", null, null, true)
      .map(_ => {
        call.cancelStatus = Call.CancelStatus.Requested;
        return call;
      });
  }

  public sendCallMessage(call: Call, message: string): Observable<CallMessage> {
    return this._http.post("/api/call/" + call.id + "/message", { Message: message }, null, true)
      .map(resp => {
        return CallService.convertCallMessageDTO2Object(this._http, resp.json());
      });
  }

  public getPagedCallsByObjectId(objectId: number, skip: number, count: number, sortBy: string, isDesc: boolean, filterOptions: CallFilterOptions = null): Observable<PagedResult<Call>> {
    let params = new URLSearchParams;

    params.append('$orderby', sortBy + (isDesc ? " desc" : " asc"));
    params.append('$select', "Id,Number,Status,CreatedInFSM,Deadline,Description,Author,IsConfirmed,CancelStatus");
    params.append('$top', count ? count.toString() : null);
    params.append('$skip', skip ? skip.toString() : null)
    params.append('$count', "true");
    params.append('$expand', "Works($expand=Object,Obstructions)");

    let filterRequestStrings: string[] = new Array();

    filterRequestStrings.push("Works/any(cso: cso/Object/Id eq " + objectId + ")");

    if (filterOptions) {

      if (filterOptions.problemTemplateIds)
        filterRequestStrings.push("ProblemTemplate/Id in (" + filterOptions.problemTemplateIds.toString() + ")");

    }

    params.append('$filter', filterRequestStrings.join(' and '));

    return this._http.get("/api/odata/calls", params, true)
      .map(resp => {
        let pagedResult = resp.json();
        let result = new PagedResult<Call>();
        result.items = [];

        for (let index in pagedResult.value) {
          result.items.push(CallService.convertCallDTO2Object(this._http, pagedResult.value[index]));
        }
        result.totalCount = pagedResult["@odata.count"];

        return result;
      });
  }
}

export namespace CallService {
  export function convertCallDTO2Object(http: HttpBackendService, dto: any, call: Call = new Call()): Call {
    call.id = dto.Id;
    call.number = dto.Number;
    call.externalNumber = dto.ExternalNumber;
    call.client = dto.Client != null ? convertClientDTO2Object(dto.Client) : null;
    call.description = dto.Description;
    call.createdInFSM = dto.CreatedInFSM;
    call.deadline = dto.Deadline == null ? null : http.parseDate(dto.Deadline);
    call.closed = dto.Closed == null ? null : http.parseDate(dto.Closed);
    call.status = convertCallStatusDTO2Object(dto.Status);
    call.cancelStatus = convertCancelStatusDTO2Object(dto.CancelStatus);
    call.author = (dto.Author != null) ? dto.Author : "Диспетчер";
    call.isConfirmed = dto.IsConfirmed;
    call.problemTemplate = dto.ProblemTemplate != null ? ProblemTemplateService.convertProblemTemplateDTO2Object(dto.ProblemTemplate) : null;
    call.works = [];
    call.reports = [];
    call.photos = [];
    call.messages = _.map(dto.Messages, function (value) {
      return convertCallMessageDTO2Object(http, value);
    })

    if (dto.Photos) {
      for (let photo of dto.Photos) {
        let newPhotoData = new CallPhotoData();
        newPhotoData.blobServiceFileName = photo.BlobServiceFileName;
        newPhotoData.type = this.convertPhotoDataTypeDTO2ObjectPhotoType(photo.Type)

        call.photos.push(newPhotoData);
      }
    }

    if (dto.Reports) {
      for (let report of dto.Reports) {
        let newReport = new Report();
        newReport.number = report.Number;
        newReport.comment = report.Comment;

        call.reports.push(newReport);
      }
    }

    if (dto.Works) {
      for (let work of dto.Works) {
        let newWork = new Work();
        newWork.obstructions = [];
        newWork.tasks = [];
        newWork.fixedAddress = work.FixedAddress;

        if (work.Tasks) {
          for (let workTask of work.Tasks) {
            let task = convertCallTaskDTO2Object(http, workTask);
            newWork.tasks.push(task);
          }
        }

        if (work.Obstructions) {
          for (let obstruction of work.Obstructions) {
            newWork.obstructions.push(convertObstructionDTO2Object(http, obstruction));
          }
        }

        newWork.serviceObject = (work.Object["@odata.type"] == "#ClientPersonalArea.Web.Domain.Model.Asset") ?
          AssetService.convertAssetDTO2Object(work.Object) : LocationService.convertLocationDTO2Object(work.Object);
        call.works.push(newWork)
      }
    }

    call.isPaused = call.works.some(x => x.obstructions.some(x => x.finishDate == null));

    return call;
  };

  export function convertCallMessageDTO2Object(http: HttpBackendService, dto: any, callMessage: CallMessage = new CallMessage()): CallMessage {
    callMessage.id = dto.Id;
    callMessage.message = dto.Message;
    callMessage.owner = (dto.Owner) ? { id: dto.Owner.Id, name: dto.Owner.Email } : null;
    callMessage.date = http.parseDate(dto.Date);

    return callMessage;
  }

  export function convertCallTaskDTO2Object(http: HttpBackendService, dto: any, workTask: WorkTask = new WorkTask()): WorkTask {
    workTask.id = dto.Id;
    workTask.title = dto.Title;
    workTask.status = convertCallTaskStatusDTO2Object(dto.Status);
    workTask.disbandmentReason = dto.DisbandmentReason;
    workTask.planned = dto.Planned == null ? null : http.parseDate(dto.Planned);
    workTask.finished = dto.Finished == null ? null : http.parseDate(dto.Finished);
    workTask.survey = dto.Survey == null ? null : convertSurveyDTO2Object(http, JSON.parse(dto.Survey));
    return workTask;
  }

  export function convertObstructionDTO2Object(http: HttpBackendService, dto: any, obstruction: Obstruction = new Obstruction()): Obstruction {
    obstruction.finishDate = dto.FinishDate != null ? http.parseDate(dto.FinishDate) : null;
    obstruction.startDate = http.parseDate(dto.StartDate);
    obstruction.reason = dto.Reason == null ? null : dto.Reason.Name;
    return obstruction;
  }

  export function convertClientDTO2Object(dto: any, client: Client = new Client()) {
    client.id = dto.Id;
    client.email = dto.Email;
    client.name = dto.Name;

    return client;
  }

  export function convertSurveyDTO2Object(http: HttpBackendService, dto: any, survey: Survey = new Survey()): Survey {
    survey.title = dto.Title;
    survey.number = dto.Number;
    survey.dateCreated = http.parseDate(dto.DateCreated, true);
    survey.sections = dto.Sections.map(x => convertSurveySectionDTO2Object(x));
    return survey;
  }

  export function convertSurveySectionDTO2Object(dto: any): Section {
    if (dto.Type == "block") {
      let section = new BlockSection();
      section.title = dto.Title;
      section.fields = dto.Fields.map(x => convertFieldDTO2Object(x));
      return section;
    } else if (dto.Type == "table") {
      let section = new TableSection();
      section.title = dto.Title;
      section.tuples = dto.Tuples.map(row => row.map(x => convertFieldDTO2Object(x)));
      return section;
    } else {
      throw Error("Unsupported section type: " + dto.Type);
    }
  }

  export function convertFieldDTO2Object(dto: any): Field {
    if (dto.Type == "text") {
      let field = new PlainTextField();
      field.title = dto.Title;
      field.value = dto.Value;
      return field;
    } else if (dto.Type == "calendarTime") {
      let field = new CalendarTimeField();
      field.title = dto.Title;
      field.value = dto.Value;
      return field;
    }  else if (dto.Type == "signature") {
      let field = new SignatureField();
      field.title = dto.Title;
      field.value = new SurveyPhoto()
      field.value.blobServiceFileName = dto.Value;
      field.value.type = PhotoType.SurveyWarrantTaskPhotoByEngineer;
      return field;
    }
    else if (dto.Type == "photo") {
      let field = new PhotoField();
      field.title = dto.Title;
      field.photos = dto.Photos == null ? null : dto.Photos.map(x => convertPhotoDTO2Object(x));
      return field;
    } else if(dto.Type == "location") {
      let field = new LocationField();
      field.title = dto.Title;
      field.value = dto.Value == null ? null : convertCoordinateDTO2Object(dto.Value);
      return field;
    } else {
      throw Error("Unsupported field type: " + dto.Type);
    }
  }

  export function convertPhotoDTO2Object(dto: any): SurveyPhoto {
    return {
      blobServiceFileName: dto.BlobServiceFileName,
      type: dto.Type
    }
  }

  export function convertCoordinateDTO2Object(dto: any): Coordinate {
    return {
      latitude: dto.Latitude,
      longitude: dto.Longitude
    }
  }

  export function convertCallStatusDTO2Object(status: string): Call.Status {
    switch (status) {
      case 'Opened': return Call.Status.Opened;
      case 'Closed': return Call.Status.Closed;
      default: return;
    }
  }

  export function convertCancelStatusDTO2Object(status: string): Call.CancelStatus {
    switch (status) {
      case 'NotRequested': return Call.CancelStatus.NotRequested
      case 'Requested': return Call.CancelStatus.Requested
      case 'Successful': return Call.CancelStatus.Successful
      case 'Unsuccessful': return Call.CancelStatus.Unsuccessful
    }
  }

  export function convertCallTaskStatusDTO2Object(status: string): WorkTask.Status {
    switch (status) {
      case 'New': return WorkTask.Status.New;
      case 'Assigned': return WorkTask.Status.Assigned;
      case 'Done': return WorkTask.Status.Done;
      case 'Canceled': return WorkTask.Status.Canceled;
      default: return;
    }
  }

  export function convertPhotoDataTypeDTO2ObjectPhotoType(photoType: string): PhotoType {
    switch (photoType) {
      case 'ContractPhotoByContractor':
        return PhotoType.ContractPhotoByContractor;
      case 'SurveyWarrantTaskPhotoByDispatcher':
        return PhotoType.SurveyWarrantTaskPhotoByDispatcher;
      case 'SurveyWarrantTaskPhotoByEngineer':
        return PhotoType.SurveyWarrantTaskPhotoByEngineer;
      case 'WarrantTaskPhotoByEngineer':
        return PhotoType.WarrantTaskPhotoByEngineer;
      default:
        return null;
    }
  }
}



// WEBPACK FOOTER //
// /app/SPA/src/app/services/call.service.ts